import React, { useState, useEffect } from "react";
import shortid from 'shortid';
import { useTranslation } from 'react-i18next';

export interface Field {
    type: string;
    label: string;
    options?: { name: string; id: string }[];
    required?: boolean;
    autoGenerate?: boolean;
}

interface AddNewProps {
    label: string;
    title: string;
    fields: Field[];
    save: string;
    onSave: (AddNewValue: { [key: string]: any }) => void;
    errorMessage?: string | null;
}

const generateUniqueId = () => {
    return shortid.generate().toUpperCase();
};

const AddNewPopUp: React.FC<AddNewProps> = ({ label, title, fields, save, onSave, errorMessage: externalErrorMessage }) => {
    const { t, i18n } = useTranslation('driver');
    const [showPopup, setShowPopup] = useState(false);
    const [addNewFormValue, setAddNewFormValue] = useState<{ [key: string]: any }>({});
    const [showOptions, setShowOptions] = useState<{ [key: string]: boolean }>({});
    const [errorMessage, setErrorMessage] = useState<string | null>(null); 
    let errorTimeout: NodeJS.Timeout | null = null;

    useEffect(() => {
        if (showPopup) {
            const initialValues: { [key: string]: any } = {};
            fields.forEach(field => {
                if (field.autoGenerate) {
                    initialValues[field.label] = generateUniqueId();
                }
            });
            setAddNewFormValue(initialValues);
        }
    }, [showPopup, fields]);

    const handleButtonClick = () => {
        setShowPopup(true);
    };

    const handleClosePopup = () => {
        setShowPopup(false);
        setErrorMessage(null); 
        clearTimeout(errorTimeout as NodeJS.Timeout); 
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setAddNewFormValue({
            ...addNewFormValue,
            [name]: value,
        });
    };

    const handleMultiSelectChange = (e: React.ChangeEvent<HTMLInputElement>, label: string) => {
        const { value, checked } = e.target;
        const parsedValue = JSON.parse(value);
        setAddNewFormValue(prevState => {
            const selectedOptions = prevState[label] || [];
            if (checked) {
                return { ...prevState, [label]: [...selectedOptions, parsedValue] };
            } else {
                return { ...prevState, [label]: selectedOptions.filter((option: any) => option.id !== parsedValue.id) };
            }
        });
    };

    const toggleOptions = (label: string) => {
        setShowOptions(prevState => ({
            ...prevState,
            [label]: !prevState[label]
        }));
    };

    const removeOption = (label: string, option: any) => {
        setAddNewFormValue(prevState => ({
            ...prevState,
            [label]: prevState[label].filter((item: any) => item.id !== option.id)
        }));
    };

    const handleSaveChanges = () => {
        const missingFields = fields.filter(field => field.required && !addNewFormValue[field.label]);
        if (missingFields.length > 0) {
            setErrorMessage(`${t('add-driver.add-driver-alert')} ${missingFields.map(field => field.label).join(", ")}`);

            if (errorTimeout) {
                clearTimeout(errorTimeout);
            }

            errorTimeout = setTimeout(() => {
                setErrorMessage(null);
            }, 3000);
        } else {
            onSave(addNewFormValue);
            setShowPopup(false);
            setErrorMessage(null); 
            clearTimeout(errorTimeout as NodeJS.Timeout); 
        }
    };

    return (
        <section>
            <button
                className="py-2 px-4 bg-primary/300 text-white rounded-md"
                onClick={handleButtonClick}
            >
                {label}
            </button>

            {showPopup && (
                <div className="fixed inset-0 flex items-center justify-center z-50 bg-gray-800 bg-opacity-50">
                    <div className="bg-white rounded-md shadow-md w-full max-w-2xl relative">
                        <button
                            className="absolute top-2 right-4 text-gray-600 hover:text-gray-800 text-title"
                            onClick={handleClosePopup}
                        >
                            &times;
                        </button>
                        <h2 className="text-subtitle-1 font-semi-bold px-6 py-4">{title}</h2>
                        
                        {/* Display error message if present */}
                        {errorMessage && (
                            <div className="px-6 py-2 text-red-500">
                                {errorMessage}
                            </div>
                        )}

                        <div className="border-y">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-y-0 px-6">
                                {fields.map((field, index) => (
                                    <div key={index} className="my-4">
                                        <label className="block mb-2">
                                            {field.label} {field.required && <span className="text-red-500">*</span>}
                                        </label>
                                        {field.type === "text" || field.type === "email" || field.type === "number" ? (
                                            <input
                                                type={field.type}
                                                name={field.label}
                                                className="border p-2 rounded-md w-full"
                                                value={addNewFormValue[field.label] || ""}
                                                onChange={handleInputChange}
                                                readOnly={field.autoGenerate}
                                            />
                                        ) : field.type === "calendar" ? (
                                            <input
                                                type="date"
                                                name={field.label}
                                                className="border p-2 rounded-md w-full"
                                                onChange={handleInputChange}
                                            />
                                        ) : field.type === "dropdown" ? (
                                            <select
                                                name={field.label}
                                                className="border p-2 rounded-md w-full"
                                                onChange={handleInputChange}
                                            >
                                                {field.options?.map((option, idx) => (
                                                    <option key={idx} value={option.name}>
                                                        {option.name}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : field.type === "multi-select" ? (
                                            <div className="rounded-md w-full relative">
                                                <div
                                                    className="border p-2 rounded-md cursor-pointer"
                                                    onClick={() => toggleOptions(field.label)}
                                                >
                                                    {addNewFormValue[field.label]?.length > 0
                                                        ? addNewFormValue[field.label].map((option: any, idx: number) => (
                                                            <div key={idx} className="inline-block bg-gray-200 rounded px-2 mr-2 mb-0">
                                                                {option.name}
                                                                <span
                                                                    className="ml-1 text-red-500 cursor-pointer"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        removeOption(field.label, option);
                                                                    }}
                                                                >
                                                                    &times;
                                                                </span>
                                                            </div>
                                                        ))
                                                        : "Select options"
                                                    }
                                                </div>
                                                {showOptions[field.label] && (
                                                    <div className="absolute mt-2 p-2 bg-white shadow-md w-full z-10 max-h-48 overflow-y-auto">
                                                        {field.options?.map((option, idx) => (
                                                            <div key={idx} className="flex items-center space-x-2">
                                                                <input
                                                                    type="checkbox"
                                                                    name={field.label}
                                                                    value={JSON.stringify(option)}
                                                                    checked={addNewFormValue[field.label]?.some((o: any) => o.id === option.id) || false}
                                                                    onChange={(e) => handleMultiSelectChange(e, field.label)}
                                                                />
                                                                <label>{option.name}</label>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ) : null}
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="flex justify-between space-x-2 px-6 py-4 font-semi-bold">
                            <button
                                className="flex-1 py-2 px-4 bg-gray-300 text-primary/300 rounded-md"
                                onClick={handleClosePopup}
                            >
                                {t('add-driver.cancel')}
                            </button>
                            <button
                                className="flex-1 py-2 px-4 bg-primary/300 text-white rounded-md"
                                onClick={handleSaveChanges}
                            >
                                {save}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

export default AddNewPopUp;